import { z } from 'zod';

/**
 * As abas do painel, **na ordem da tela** — a ordem mora aqui, e não no JSON
 * publicado: é decisão de produto, e não pode depender de como alguém digitou
 * a configuração no console.
 */
export const PAINEL_TABS = [
  'ensaio',
  'gabarito',
  'ranking',
  'prova-oficial',
  'diagnostico',
] as const;

export type PainelTab = (typeof PAINEL_TABS)[number];

/** A chave de cada aba no Remote Config (`EXAMS_CAPTURE_TABS`). */
export const TAB_CONFIG_KEY: Record<PainelTab, string> = {
  ensaio: 'ENSAIO',
  gabarito: 'GABARITO',
  ranking: 'RANKING',
  'prova-oficial': 'PROVA_OFICIAL',
  diagnostico: 'DIAGNOSTICO',
};

/**
 * O estado de uma aba no Remote Config — o mesmo contrato do ENAMED.
 * Booleanos separados porque são decisões separadas.
 */
export const TabConfigSchema = z.object({
  /** Se a aba aparece. Desligada, nem o botão nem o conteúdo existem. */
  active: z.boolean(),
  /** Se é a aba aberta ao entrar no painel. Só vale em aba `active`. */
  default: z.boolean().optional(),
  /** "Em breve": aparece desabilitada e não abre. Ausente = `false`. */
  preview: z.boolean().optional(),
});

export type TabConfig = z.infer<typeof TabConfigSchema>;

/** As abas de uma prova e ano. Toda chave é opcional (ver `withDefaults`). */
export const EditionTabsConfigSchema = z.record(z.string(), TabConfigSchema);

/**
 * O JSON de `EXAMS_CAPTURE_TABS`: prova → ano → abas, ex.
 * `{ "hcpa": { "2026": { "ENSAIO": { "active": true, "default": true } } } }`.
 * Uma chave para todas as provas: os quatro sites leem a mesma.
 */
export const ExamsCaptureTabsConfigSchema = z.record(
  z.string(),
  z.record(z.string(), EditionTabsConfigSchema),
);

export type ExamsCaptureTabsConfig = z.infer<
  typeof ExamsCaptureTabsConfigSchema
>;

/** As abas de uma edição, já por aba do painel. */
export type EditionTabs = Record<PainelTab, TabConfig>;

/**
 * **O piso seguro**: só o Ensaio, aberto. É o que vale quando a chave não
 * existe, não responde, vem quebrada ou não tem a prova e o ano — antes da
 * prova não há gabarito, ranking nem prova oficial a mostrar, e ligar abas por
 * engano seria pior do que esconder.
 */
export const DEFAULT_EDITION_TABS: EditionTabs = {
  ensaio: { active: true, default: true },
  gabarito: { active: false },
  ranking: { active: false },
  'prova-oficial': { active: false },
  diagnostico: { active: false },
};

/** Todas ligadas: o painel com mocks, para o desenvolvimento local. */
export const ALL_TABS_ON: EditionTabs = {
  ensaio: { active: true, default: true },
  gabarito: { active: true },
  ranking: { active: true },
  'prova-oficial': { active: true },
  diagnostico: { active: true },
};

/**
 * As abas da prova e do ano no JSON publicado, **aba a aba por cima do piso**:
 * aba ausente no JSON fica com o valor do piso (Ensaio ligado, o resto
 * desligado), então publicar só o que muda não derruba as outras. Chave de aba
 * desconhecida é ignorada.
 */
export function editionTabsFrom(
  config: ExamsCaptureTabsConfig,
  tenant: string,
  year: number,
): EditionTabs {
  const published = config[tenant]?.[String(year)];
  if (!published) return DEFAULT_EDITION_TABS;

  return Object.fromEntries(
    PAINEL_TABS.map((tab) => [
      tab,
      published[TAB_CONFIG_KEY[tab]] ?? DEFAULT_EDITION_TABS[tab],
    ]),
  ) as EditionTabs;
}

export interface ResolvedTabs {
  /** As abas a desenhar, na ordem da tela. */
  visible: PainelTab[];
  /** As de `visible` que aparecem como "Em breve" (desabilitadas). */
  preview: PainelTab[];
  /** A aba aberta ao entrar; `null` se nenhuma abre. */
  initial: PainelTab | null;
}

/**
 * Quais abas desenhar e qual abrir — mesmas regras do ENAMED: sem `default`,
 * abre a primeira ligada; com mais de um, vence o primeiro na ordem da tela;
 * aba "Em breve" nunca é a inicial.
 */
export function resolveTabs(tabs: EditionTabs): ResolvedTabs {
  const visible = PAINEL_TABS.filter((tab) => tabs[tab].active);
  const preview = visible.filter((tab) => tabs[tab].preview === true);
  const openable = visible.filter((tab) => !preview.includes(tab));
  return {
    visible,
    preview,
    initial:
      openable.find((tab) => tabs[tab].default === true) ?? openable[0] ?? null,
  };
}
